import React, { useState, useRef } from 'react';
import Container from '../../layout/Container';
import ContactForm from './ContactForm';

const Contact = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  const handleMouseMove = (e) => {
    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      setMousePosition({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 20,
      });
    }
  };

  return (
    <section 
      id="contato"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="py-16 md:py-20 relative overflow-hidden bg-gray-950"
    >
      {/* Background com parallax */}
      <div className="absolute inset-0">
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl transition-transform duration-500"
          style={{
            transform: `translate(${mousePosition.x * 2}px, ${mousePosition.y * 2}px)`,
          }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl transition-transform duration-500"
          style={{
            transform: `translate(${-mousePosition.x * 2}px, ${-mousePosition.y * 2}px)`,
          }}
        />
      </div>

      {/* Grid animado */}
      <div 
        className="absolute inset-0 opacity-5 transition-transform duration-300"
        style={{
          transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`,
          backgroundImage: `
            linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center relative z-10">
          {/* Left side - Text */}
          <div className="order-2 lg:order-1">
            <div className="relative group">
              <div 
                className="absolute -inset-4 bg-emerald-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{
                  transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`,
                }}
              />

              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 group-hover:text-emerald-500 transition-colors duration-500">
                  Fale com a gente_
                </h2>
                <p className="text-base md:text-lg text-gray-400 mb-6 leading-relaxed">
                  Tem um projeto em mente? Quer tirar dúvidas? Ou só trocar uma ideia? 
                  Preencha o formulário e a gente responde rapidinho.
                </p>

                {/* Info de contato */}
                <div className="space-y-4">
                  <a 
                    href="mailto:maralabssoftware@gmail.com"
                    className="flex items-center text-gray-400 group/item hover:text-emerald-500 transition-all duration-300 hover:translate-x-2"
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-xl opacity-0 group-hover/item:opacity-100 transition-opacity duration-500" />
                      <svg className="w-6 h-6 mr-3 text-emerald-500 relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-sm">maralabssoftware@gmail.com</span>
                  </a>

                  <div className="flex items-center text-gray-400 group/item hover:text-emerald-500 transition-all duration-300 hover:translate-x-2">
                    <div className="relative">
                      <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-xl opacity-0 group-hover/item:opacity-100 transition-opacity duration-500" />
                      <svg className="w-6 h-6 mr-3 text-emerald-500 relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className="text-sm">São Luís, MA - Brasil</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Card com Form */}
          <div className="order-1 lg:order-2">
            <div 
              className="relative bg-gray-900/50 border border-gray-800 rounded-2xl p-6 md:p-8 transition-all duration-700 hover:border-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/10 group"
              style={{
                transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)',
              }}
            >
              {/* Borda animada */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div 
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: `linear-gradient(135deg, transparent, rgba(16, 185, 129, 0.2), transparent)`,
                    padding: '1px',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />
              </div>

              {/* Shimmer effect */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/3 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-2000" />
              </div>

              <ContactForm />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Contact;